import { api } from "@/lib/axios";
import type { Comment, PostResponse } from "@/lib/humhub-api/types";

export const STREAM_ACTIVITIES_READ_EVENT = "stream-activities-read";

export type StreamActivityType = "post" | "comment" | "reply";
export type StreamReadSource = "stream_view" | "post_detail" | "catch_up_check";

export type CatchUpItem = {
  id: number;
  spaceId: number;
  postId: number;
  postContentId: number;
  activityType: StreamActivityType;
  activityId: number;
  parentCommentId: number | null;
  channelId?: number | null;
  createdBy: number;
  createdAt: string;
  isMention?: boolean;
  summary?: string;
  actor?: {
    id: number;
    displayName: string;
    imageUrl: string;
  } | null;
};

export type StreamActivityContext = {
  post: PostResponse;
  targetComment: Comment | null;
  parentComment: Comment | null;
};

const isPositiveId = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value > 0;

const parseOptionalPositiveId = (value: unknown): number | null => {
  if (isPositiveId(value)) return value;
  if (typeof value !== "string" || !/^[1-9][0-9]*$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

const normalizeIds = (values: readonly number[]): number[] =>
  [...new Set(values.filter(isPositiveId))].slice(0, 200);

const parseActivityIds = (value: unknown): number[] => {
  if (
    typeof value !== "object" ||
    value === null ||
    !("activity_ids" in value) ||
    !Array.isArray(value.activity_ids) ||
    !value.activity_ids.every(isPositiveId)
  ) {
    throw new Error("Malformed stream read response");
  }
  return value.activity_ids;
};

const parseCatchUpItem = (value: unknown): CatchUpItem => {
  if (typeof value !== "object" || value === null) {
    throw new Error("Malformed catch-up response");
  }
  const item = value as Record<string, unknown>;
  const activityType = item.activity_type;
  const actorValue =
    typeof item.actor === "object" && item.actor !== null
      ? (item.actor as Record<string, unknown>)
      : null;
  const actorId = Number(actorValue?.id);
  if (
    !isPositiveId(item.id) ||
    !isPositiveId(item.space_id) ||
    !isPositiveId(item.post_id) ||
    !isPositiveId(item.post_content_id) ||
    !["post", "comment", "reply"].includes(String(activityType)) ||
    !isPositiveId(item.activity_id) ||
    !(
      item.parent_comment_id === null || isPositiveId(item.parent_comment_id)
    ) ||
    !isPositiveId(item.created_by) ||
    typeof item.created_at !== "string"
  ) {
    throw new Error("Malformed catch-up response");
  }
  return {
    id: item.id,
    spaceId: item.space_id,
    postId: item.post_id,
    postContentId: item.post_content_id,
    activityType: activityType as StreamActivityType,
    activityId: item.activity_id,
    parentCommentId: item.parent_comment_id,
    channelId: parseOptionalPositiveId(item.channel_id),
    createdBy: item.created_by,
    createdAt: item.created_at,
    isMention:
      item.is_mention === true ||
      item.is_mention === 1 ||
      item.is_mention === "1",
    summary: typeof item.summary === "string" ? item.summary : "",
    actor:
      actorValue && Number.isSafeInteger(actorId) && actorId > 0
        ? {
            id: actorId,
            displayName:
              typeof actorValue.display_name === "string"
                ? actorValue.display_name
                : "",
            imageUrl:
              typeof actorValue.image_url === "string"
                ? actorValue.image_url
                : "",
          }
        : null,
  };
};

const parseActivityContext = (value: unknown): StreamActivityContext => {
  if (typeof value !== "object" || value === null) {
    throw new Error("Malformed stream activity context response");
  }
  const data = value as Record<string, unknown>;
  if (typeof data.post !== "object" || data.post === null) {
    throw new Error("Malformed stream activity context response");
  }
  const parseComment = (comment: unknown): Comment | null => {
    if (comment === null || comment === undefined) return null;
    if (
      typeof comment !== "object" ||
      !("id" in comment) ||
      !isPositiveId(comment.id)
    ) {
      throw new Error("Malformed stream activity context response");
    }
    return comment as Comment;
  };
  return {
    post: data.post as PostResponse,
    targetComment: parseComment(data.target_comment),
    parentComment: parseComment(data.parent_comment),
  };
};

export const streamReadApi = {
  getActivityContext: async (
    spaceId: number,
    activityId: number,
  ): Promise<StreamActivityContext> => {
    const response = await api.get(`/stream/read/context/${activityId}`, {
      params: { spaceId },
    });
    return parseActivityContext(response.data);
  },

  getCatchUp: async (
    spaceId: number,
    options: { includeComments?: boolean; limit?: number } = {},
  ): Promise<CatchUpItem[]> => {
    const response = await api.get("/stream/read/catch-up", {
      params: {
        spaceId,
        include_comments: options.includeComments ?? false,
        limit: Math.max(1, Math.min(100, options.limit ?? 20)),
      },
    });
    const data = response.data as unknown;
    if (
      typeof data !== "object" ||
      data === null ||
      !("results" in data) ||
      !Array.isArray(data.results)
    ) {
      throw new Error("Malformed catch-up response");
    }
    return data.results.map(parseCatchUpItem);
  },

  markRead: async (
    spaceId: number,
    activityIds: readonly number[],
    source: StreamReadSource,
  ): Promise<number[]> => {
    const response = await api.post(
      "/stream/read/mark-read",
      { activity_ids: normalizeIds(activityIds), source },
      { params: { spaceId } },
    );
    return parseActivityIds(response.data);
  },

  markUnread: async (
    spaceId: number,
    activityIds: readonly number[],
  ): Promise<number[]> => {
    const response = await api.post(
      "/stream/read/mark-unread",
      { activity_ids: normalizeIds(activityIds) },
      { params: { spaceId } },
    );
    return parseActivityIds(response.data);
  },
};
